/**
 * The parts every page is drawn with.
 *
 * THE LOOK, 2026-09-16 (mockup HIL_CENTRAL_LOOK_MOCKUP_2026-09-15.html, agreed
 * with Darek). The screens carried the right data in a dozen card styles, eight
 * text sizes and a new banner per page: STYLE_GUIDE.md said what a page should
 * read like, and nothing drew it. These do, and a page that uses them cannot
 * drift from the next one.
 *
 *   PageHead   the page's name small, its answer big, one plain sentence
 *   Section    a titled part of a page
 *   Board/Row  one row shape for every list: the state as a word, the thing,
 *              its detail, the time or the action
 *   Group      a heading inside a board
 *   Status     a result or a state as a dot AND a word
 *   Counts     the numbers, each with its word, on one line
 *   Facts      labelled pairs instead of a string of middle dots
 *   Views      one control for the views of a page
 *   Finding    what went wrong, under the row it belongs to
 *   Notice     a sentence the reader must not miss
 *
 * Buttons are classes, `btn-primary`, `btn-quiet` and `btn-danger` (index.css),
 * because a button is an element with its own semantics, not a wrapper.
 *
 * The answer is not a banner. A Dark Blue band with the answer in yellow was
 * tried in the mockup and rejected ("looks like a page header"): it sits on
 * the page, Dark Blue, Warm Red when something failed.
 */
import type React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { bucketOf } from "./Verdict";

export type Tone = "good" | "bad" | "warn" | "off" | "live" | "plain";

export const DOT: Record<Tone, string> = {
  good: "bg-success-500",
  bad: "bg-red-500",
  warn: "bg-amber-400",
  off: "bg-slate-300 dark:bg-slate-600",
  live: "bg-sky-500",
  plain: "bg-brand-700 dark:bg-sky-300",
};

/** Text in a tone. Contrast checked on white and on the dark card (palette.test.ts). */
export const INK: Record<Tone, string> = {
  good: "text-success-700 dark:text-success-400",
  bad: "text-red-700 dark:text-red-400",
  warn: "text-amber-700 dark:text-amber-400",
  off: "text-slate-500 dark:text-slate-400",
  live: "text-sky-700 dark:text-sky-300",
  plain: "text-slate-900 dark:text-slate-100",
};

/** A verdict's tone, by the same four buckets every count on every page uses. */
export function toneOfVerdict(verdict: string): Tone {
  const b = bucketOf(verdict);
  return b === "failed" ? "bad" : b === "xfailed" ? "warn" : b === "skipped" ? "off" : "good";
}

/** The top of a page: where it sits, what it is, and what it has to say. */
/** One level of the breadcrumbs; the last has no `to` - it is this page. */
export type Crumb = { label: React.ReactNode; to?: string };

/** Where this page sits: DisplayOS › Builds › 7.2.72 › Campaign #4 › run48.
 *  K1 of the rebuild promised crumbs on every page; the build, campaign and run
 *  pages had a single "back" link, so the way up from a run was the browser. */
export function Crumbs({ items }: { items: Crumb[] }) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("common.crumbs")} className="flex flex-wrap items-baseline gap-x-1.5 text-sm text-slate-500 dark:text-slate-400">
      {items.map((c, i) => (
        <span key={i} className="inline-flex items-baseline gap-x-1.5">
          {i > 0 && <span aria-hidden="true">›</span>}
          {c.to ? (
            <Link className="hover:text-slate-800 hover:underline dark:hover:text-slate-200" to={c.to}>{c.label}</Link>
          ) : (
            <span aria-current="page" className="font-medium text-slate-700 dark:text-slate-300">{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function PageHead({
  back,
  crumbs,
  title,
  answer,
  bad,
  after,
  actions,
  children,
}: {
  back?: { to: string; label: string };
  /** Breadcrumbs; drawn instead of `back` when given. */
  crumbs?: Crumb[];
  title: React.ReactNode;
  /** One sentence that answers the page's question. The biggest text on it. */
  answer?: React.ReactNode;
  /** Something failed: the answer is Warm Red instead of Dark Blue. */
  bad?: boolean;
  /** One plain sentence under the answer: what, where, when, who. */
  after?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <header className="mb-6">
      {crumbs && crumbs.length > 0 ? <Crumbs items={crumbs} /> : back && (
        <Link className="text-sm text-slate-500 underline hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
              to={back.to}>
          {back.label}
        </Link>
      )}
      <div className={`flex flex-wrap items-start gap-x-4 gap-y-3 ${back || crumbs?.length ? "mt-2" : ""}`}>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg text-slate-700 dark:text-slate-300">{title}</h1>
          {answer && (
            <div
              data-testid="page-answer"
              className={`mt-1 max-w-4xl text-2xl font-bold leading-tight [text-wrap:balance] sm:text-3xl ${
                bad ? "text-red-700 dark:text-red-400" : "text-brand-700 dark:text-sky-200"
              }`}
            >
              {answer}
            </div>
          )}
          {after && (
            <div className="mt-2 max-w-prose text-base text-slate-700 dark:text-slate-300">{after}</div>
          )}
        </div>
      </div>
      {/* Under the answer, as the approved demo has them, not beside it: beside
          it the answer was squeezed into a column and broke as "Known
          problems: / 3." on the product Overview. */}
      {actions && <div className="mt-4 flex max-w-full flex-wrap items-center gap-2">{actions}</div>}
      {children}
    </header>
  );
}

/** A titled part of a page. */
export function Section({
  title,
  note,
  actions,
  children,
  id,
  card = false,
}: {
  title: React.ReactNode;
  note?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  id?: string;
  /** Drawn as a card: a white panel with a small grey heading, as the approved
   *  demo lays out the case, build and Overview pages. A board of rows inside
   *  runs edge to edge in it instead of drawing a second frame. */
  card?: boolean;
}) {
  if (card) {
    return (
      <section
        id={id}
        className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 [&_.board]:-mx-4 [&_.board]:-mb-4 [&_.board]:rounded-none [&_.board]:border-x-0 [&_.board]:border-b-0 [&_.board]:shadow-none"
      >
        <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          {title ? <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-300">{title}</h2> : null}
          {note && <span className="text-sm text-slate-500 dark:text-slate-400">{note}</span>}
          {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {children}
      </section>
    );
  }
  return (
    <section className="mt-8" id={id}>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {/* A section with no title draws none. The Failures view of a run had
            a heading saying "Failed" directly under a tab saying "Failures 3",
            and a heading that repeats the control above it is furniture. */}
        {title ? (
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
        ) : null}
        {note && <span className="text-sm text-slate-500 dark:text-slate-400">{note}</span>}
        {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/** A card that opens: what most readers skip, one click away (the steps as
 *  written under a case page). */
export function Fold({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <details className="mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <summary className="cursor-pointer text-sm font-semibold text-slate-600 dark:text-slate-300">{title}</summary>
      <div className="mt-2">{children}</div>
    </details>
  );
}

/** The frame of a list of rows. `as="ul"` where the rows are real list items
 *  (a case list, where each row is an <li> somebody can link to). */
export function Board({
  children,
  className = "",
  as: Tag = "div",
}: {
  children: React.ReactNode;
  className?: string;
  as?: "div" | "ul";
}) {
  return (
    <Tag className={`board divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900 ${className}`}>
      {children}
    </Tag>
  );
}

/** A heading inside a board: the group the rows below it belong to. */
export function Group({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-slate-50 px-4 py-2 text-sm text-slate-600 dark:bg-slate-950/40 dark:text-slate-400">
      {children}
    </div>
  );
}

/**
 * One row of a board. On a desktop: the state in its own column, the thing
 * and its detail, and the time or the actions on the right. On a phone the
 * same parts stack in the same order, so the state is still read first.
 */
export function Row({
  status,
  title,
  sub,
  aside,
  children,
  id,
}: {
  status?: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  aside?: React.ReactNode;
  /** Under the row, aligned with its title: a finding, an opened detail. */
  children?: React.ReactNode;
  id?: string;
}) {
  const cols = status
    ? "sm:grid-cols-[10rem_minmax(0,1fr)_auto]"
    : "sm:grid-cols-[minmax(0,1fr)_auto]";
  return (
    <div id={id} className="px-4 py-3">
      <div className={`grid gap-x-4 gap-y-1 sm:items-baseline ${cols}`}>
        {status && <div>{status}</div>}
        <div className="min-w-0">
          <div className="break-words text-base text-slate-900 dark:text-slate-100">{title}</div>
          {sub && <div className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{sub}</div>}
        </div>
        {aside && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400 sm:justify-end">
            {aside}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

/** A state or a result as a dot and a word. Never the dot alone. */
export function Status({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  return (
    <span title={title} className={`inline-flex items-baseline gap-2 font-semibold ${INK[tone]}`}>
      <span aria-hidden="true" className={`inline-block h-2.5 w-2.5 shrink-0 self-center rounded-full ${DOT[tone]}`} />
      {/* Sentence case whatever the word arrives as: verdict words are shared
          with running text ("3 failed"), so they are lower case in the catalogue. */}
      <span className="inline-block first-letter:uppercase">{children}</span>
    </span>
  );
}

export type Count = {
  n: number;
  /** The whole phrase in the reader's language, number included: "3 failed". */
  label: string;
  tone: Tone;
  active?: boolean;
  onClick?: () => void;
  title?: string;
};

/** The number inside a phrase, emphasised wherever the language puts it. */
function Emphasised({ c }: { c: Count }) {
  const at = c.label.indexOf(String(c.n));
  const ink = c.n === 0 && c.tone !== "plain" ? INK.off : INK[c.tone];
  if (at < 0) return <span className={ink}>{c.label}</span>;
  return (
    <>
      {c.label.slice(0, at)}
      <b className={`text-2xl font-bold tabular-nums ${ink}`}>{c.n}</b>
      {c.label.slice(at + String(c.n).length)}
    </>
  );
}

/**
 * The counts of a page on one line, each number with its word. A count that
 * filters the page below is a button saying whether it is on.
 */
export function Counts({ items, testId }: { items: Count[]; testId?: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2 text-base text-slate-700 dark:text-slate-300"
         data-testid={testId}>
      {items.map((c, i) => (
        <span key={c.label} className="contents">
          {i > 0 && " "}
          {c.onClick ? (
            <button
              type="button"
              onClick={c.onClick}
              aria-pressed={!!c.active}
              title={c.title}
              className={`-mx-1.5 rounded-md px-1.5 py-0.5 ${
                c.active
                  ? "bg-sky-50 ring-1 ring-sky-400 dark:bg-sky-950/50 dark:ring-sky-700"
                  : "hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <Emphasised c={c} />
            </button>
          ) : (
            <span><Emphasised c={c} /></span>
          )}
        </span>
      ))}
    </div>
  );
}

/** Labelled facts, a label beside its value (above it on a phone). */
export function Facts({ items }: { items: [React.ReactNode, React.ReactNode][] }) {
  return (
    <dl className="grid max-w-3xl grid-cols-1 gap-x-4 text-sm sm:grid-cols-[max-content_minmax(0,1fr)] sm:gap-y-1">
      {items.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="mt-2 text-slate-500 dark:text-slate-400 sm:mt-0">{k}</dt>
          <dd className="break-words text-slate-800 dark:text-slate-200">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The views of a page, as one control. Tabs to a screen reader. */
export function Views<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: [T, string][];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="inline-flex max-w-full flex-wrap overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
    >
      {items.map(([id, text], i) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={value === id}
          onClick={() => onChange(id)}
          className={`px-3.5 py-1.5 text-sm ${i ? "border-l border-slate-200 dark:border-slate-800" : ""} ${
            value === id
              ? "bg-brand-50 font-semibold text-brand-700 dark:bg-brand-800 dark:text-sky-100"
              : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
          }`}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/** What went wrong, under the row it belongs to, aligned with the row's title. */
export function Finding({ tone = "bad", children }: { tone?: "bad" | "warn"; children: React.ReactNode }) {
  return (
    <div
      className={`mt-2 rounded-r-md border-l-[3px] px-3 py-2 text-sm sm:ml-[11rem] ${
        tone === "bad"
          ? "border-red-500 bg-red-50 text-red-950 dark:bg-red-950/40 dark:text-red-100"
          : "border-amber-400 bg-amber-50 text-amber-950 dark:bg-amber-950/40 dark:text-amber-100"
      }`}
    >
      {children}
    </div>
  );
}

/** A sentence the reader must not miss: a refusal, a warning, a note. */
/**
 * Something is on its way.
 *
 * A page that fetches shows this until it can answer. Most pages showed
 * NOTHING: their rows start as an empty array, which cannot tell "not here
 * yet" from "there are none", so an empty screen meant both and a slow network
 * looked like a bench with no runs.
 *
 * A word AND a turning ring, never the ring alone: the word is what a screen
 * reader announces and what somebody with motion turned off is left with.
 * `role="status"` with a polite live region says it once, when it appears,
 * rather than interrupting. The ring stops turning under
 * prefers-reduced-motion and stays as a plain circle.
 *
 * The label comes in rather than out of i18n, because this file is the look and
 * knows no words; the page passes t("common.loading").
 */
export function Loading({ label, className = "" }: { label: string; className?: string }) {
  return (
    <p
      role="status"
      aria-live="polite"
      className={`mt-6 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 ${className}`}
    >
      <span
        aria-hidden="true"
        className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500 motion-reduce:animate-none dark:border-slate-700 dark:border-t-slate-400"
      />
      {label}
    </p>
  );
}

export function Notice({
  tone = "note",
  children,
}: {
  tone?: "error" | "warn" | "note";
  children: React.ReactNode;
}) {
  const look =
    tone === "error"
      ? "border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
      : tone === "warn"
        ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200"
        : "border-slate-200 bg-white text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300";
  return (
    <div role={tone === "error" ? "alert" : undefined} className={`mt-3 rounded-md border px-3 py-2 text-sm ${look}`}>
      {children}
    </div>
  );
}
